// ═══════════════════════════════════════════════════════════════════════════
// ReportsStorePage.js — Layer 3: Vedic Reports Store
// Listing page for all 9 one-time paid reports. Shows previews so users
// can decide before purchasing. Razorpay wired later.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from "react";
import TrueSelfReportTab from "./TrueSelfReportTab";
import ReportViewerTab from "./ReportViewerTab";
import MuhurtaReportTab from "./MuhurtaReportTab";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// Matches JyotishPage.js's JT token system — white, one accent, plain Inter.
const RT = {
  page:     "#ffffff",
  card:     "#ffffff",
  cardWarm: "#fafafa",
  border:   "#e2e2e2",
  shadow:   "0 1px 3px rgba(0,0,0,0.08)",
  font:     "'Inter',sans-serif",
  text:     "#1a1a1a",
  textDim:  "#5a5a5a",
  accent:   "#c2560f",
};

// ── Report catalogue ─────────────────────────────────────────────────────────
// Each report keeps a distinct, readable category color (used sparingly —
// icon, badge, button — never as a competing page background).
const REPORTS = [
  // ── ₹99 Tier ──
  {
    id:       "true_self",
    title:    "Your True Self",
    subtitle: "A Vedic Portrait",
    price:    99,
    icon:     "🔱",
    color:    "#6d28d9",
    status:   "available",
    tagline:  "Who you really are — beyond your sun sign.",
    preview:  "Three layers of identity — how the world sees you, how you feel inside, and what your soul is made of — synthesised into one clear portrait from your Janma Kundali.",
    includes: [
      "Your 3-layer identity (Lagna · Moon · Sun synthesis)",
      "Your Birth Star — deity, gift, and life pattern",
      "Your Soul Planet (Atmakaraka) + all active yogas",
      "Natural strengths and growth edges",
      "Your dharmic path with a personalised Gita verse",
    ],
    sample: "\"You carry an intriguing paradox — a disciplined outer self that meets the world with structure, while an inquisitive, restless mind searches for meaning beneath the surface...\"",
  },
  {
    id:       "power_purpose",
    title:    "Your Power & Purpose",
    subtitle: "Reading",
    price:    99,
    icon:     "⚡",
    color:    "#a16207",
    status:   "available",
    tagline:  "What you were built to achieve.",
    preview:  "Goes beyond career — this is about the kind of authority and impact you carry naturally. Your chart shows exactly what kind of power you hold and how it wants to be used.",
    includes: [
      "Your natural authority style from the birth chart",
      "The planetary combination shaping your ambition",
      "Where your drive finds its highest expression",
      "How your current dasha activates your purpose",
      "The Gita archetype that governs your power",
    ],
    sample: "\"Your chart shows a rare combination — the philosopher and the doer in the same person. You are not built for ordinary ambition...\"",
  },
  {
    id:       "body_energy",
    title:    "Your Body & Energy",
    subtitle: "Reading",
    price:    99,
    icon:     "🌿",
    color:    "#15803d",
    status:   "available",
    tagline:  "Your Ayurvedic constitution from the stars.",
    preview:  "Your birth chart is also a health map. Each planet governs a body system. This reading reveals your energetic constitution, your natural low periods, and the practices that restore you.",
    includes: [
      "Your Ayurvedic constitution from the birth chart",
      "Which planets govern your vitality and health",
      "Your energetic peaks and dips through the year",
      "Specific practices for your planetary constitution",
      "Foods, timing, and rhythms that restore you",
    ],
    sample: "\"Three planets in Gemini give you a Vata-dominant constitution — restless, creative, quick — but prone to depletion when you push without rest...\"",
  },

  // ── ₹149 Tier ──
  {
    id:       "career",
    title:    "Your Life's Work",
    subtitle: "Career Reading",
    price:    149,
    icon:     "🏆",
    color:    "#4338ca",
    status:   "available",
    tagline:  "When to push, when to wait, and what you're built for.",
    preview:  "Not just what career suits you — but when. This report combines your natal chart with your dasha timeline to show you career windows, the right environment, and your ideal role.",
    includes: [
      "Your ideal work environment and role from the chart",
      "Career timing windows from dasha analysis",
      "The planetary combinations shaping your professional path",
      "When your next major career shift is indicated",
      "How to align your work with your dharma",
    ],
    sample: "\"Mercury–Sun conjunction in your chart gives you a rare ability to lead with ideas. The coming Mercury dasha (2034–2051) is your professional peak window...\"",
  },
  {
    id:       "year_ahead",
    title:    "Your Year Ahead",
    subtitle: "Next 12 months",
    price:    149,
    icon:     "📅",
    color:    "#0f766e",
    status:   "available",
    tagline:  "What the next 12 months are actually about for you.",
    preview:  "A 3-phase breakdown of the 12 months from today — four months at a time — based on your dasha, Jupiter and Saturn transits, and natal chart. Love, career, health, finances — what each area holds.",
    includes: [
      "3-phase breakdown of the next 12 months (4 months each)",
      "Key opportunity and caution periods",
      "Love, career, health, and finances — area by area",
      "The dasha themes shaping this year",
      "One Gita insight for the year's central challenge",
    ],
    sample: "\"2026 opens under Saturn Moon antardasha — a period of emotional depth and discipline. The real breakthrough arrives in Q3 when Jupiter shifts...\"",
  },
  {
    id:       "next_chapter",
    title:    "Your Next Chapter",
    subtitle: "6-Month Reading",
    price:    149,
    icon:     "🌅",
    color:    "#c2410c",
    status:   "available",
    tagline:  "The next 6 months decoded.",
    preview:  "What are the next 6 months energetically about for you? This report gives you the overarching theme, key activation periods, and specific actions aligned with your dasha.",
    includes: [
      "The overarching theme of your next 6 months",
      "Month-by-month energy and key dates",
      "Which life area is being most activated",
      "How to use this period for maximum growth",
      "Specific actions aligned with your dasha",
    ],
    sample: "\"The next 6 months are a threshold — Saturn is testing your foundations while Jupiter prepares to open a door you've been waiting for...\"",
  },

  {
    id:       "muhurta",
    title:    "Your Event Muhurta",
    subtitle: "Auspicious Date & Time Report",
    price:    149,
    icon:     "🕐",
    color:    "#a16207",
    status:   "available",
    tagline:  "The right day AND the right time — for the event that matters most.",
    preview:  "Jyotish has always been about timing. This report finds your top 3–5 genuinely auspicious dates for a specific life event — marriage, business, property, career, travel, study, or medical — with the exact Choghadiya window to begin. Not just 'good days'. Exact times.",
    includes: [
      "Top 3–5 personally auspicious dates (Tara + Chandra Bala + Dasha scored)",
      "Exact Choghadiya start windows for each date",
      "Abhijit Muhurta (solar noon window) highlighted where relevant",
      "Rahu Kalam times to avoid — clearly marked",
      "Activity-specific Vedic ritual guidance for the day itself",
    ],
    sample: "\"Wednesday, 14 May 2025 is your strongest date. Begin the ceremony between 9:00–10:30 AM IST (Labh Choghadiya). Rohini nakshatra supports lasting bonds. Avoid 10:30–12:00 — Rahu Kalam falls here...\"",
  },

  // ── ₹179 Tier ──
  {
    id:       "relationship",
    title:    "Your Relationship Blueprint",
    subtitle: "Reading",
    price:    179,
    icon:     "💞",
    color:    "#be185d",
    status:   "available",
    tagline:  "What you need in love — and why.",
    preview:  "Your Venus placement, 7th house lord, and Navamsha (soul chart) together reveal your deepest relationship patterns — what you're drawn to, what hurts you, and what you actually need.",
    includes: [
      "What you need in a partner based on your chart",
      "Your Venus and what it reveals about love",
      "Your relationship patterns and where they come from",
      "How your current dasha affects relationships",
      "Your ideal timing for deepening commitment",
    ],
    sample: "\"Venus in Mrigashira makes you a seeker in love — always searching for something a little deeper, a little more real. The right relationship won't feel safe immediately; it will feel alive...\"",
  },
  {
    id:       "karmic",
    title:    "Your Karmic Blueprint",
    subtitle: "Past Life Reading",
    price:    179,
    icon:     "🌀",
    color:    "#7e22ce",
    status:   "available",
    tagline:  "What you brought in — and what you came to build.",
    preview:  "Rahu and Ketu are the soul's axis. Ketu holds your past-life mastery. Rahu holds this life's mission. This report decodes both — and explains the karmic patterns that keep repeating.",
    includes: [
      "What Ketu reveals about your past-life gifts",
      "What Rahu reveals about your soul's mission this life",
      "Your karmic patterns and why they repeat",
      "Where you are repaying and building karma",
      "How to work consciously with your karmic inheritance",
    ],
    sample: "\"Ketu in Sagittarius tells a story of a past life immersed in philosophy, possibly a teacher or sage. You brought that wisdom in — but Rahu in Gemini now asks you to communicate it...\"",
  },

  // ── ₹249 Tier ──
  {
    id:       "compatibility",
    title:    "Your Compatibility Reading",
    subtitle: "with [Partner Name]",
    price:    249,
    icon:     "💑",
    color:    "#be123c",
    status:   "coming_soon",
    tagline:  "Two charts. One complete picture.",
    preview:  "Full Ashtakoota + chart overlay between you and a partner. Goes deeper than a score — shows where you harmonise, where friction lives, and how your combined dasha timing affects the relationship.",
    includes: [
      "Full Ashtakoota (8-factor) match with your partner",
      "Where you naturally harmonise and where friction lives",
      "How each person's dasha affects the relationship",
      "Your combined peak timing windows",
      "A shared dharmic purpose from both charts",
    ],
    sample: "\"Your Gajakesari Yoga meets their Shukra-Mangal combination — what looks like opposing energies is actually a rare complementary force...\"",
  },
];

const PRICE_TIERS = [
  { price: 99,  label: "₹99",  desc: "Deep personal profiles" },
  { price: 149, label: "₹149", desc: "Timing & life direction" },
  { price: 179, label: "₹179", desc: "Relationships & karma" },
  { price: 249, label: "₹249", desc: "Two-chart readings" },
];

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

// ── Single report card ───────────────────────────────────────────────────────
function ReportCard({ report: r, onOpen, hasGenerated, isSubscribed, onUpgrade }) {
  const [expanded, setExpanded] = useState(false);
  const isAvailable = r.status === "available";

  return (
    <div style={{
      borderRadius: 16,
      border: `1px solid ${expanded ? r.color + "60" : RT.border}`,
      background: expanded ? `rgba(${hexToRgb(r.color)},0.04)` : RT.card,
      overflow: "hidden",
      transition: "all 0.2s",
    }}>

      {/* ── Card header ── */}
      <div style={{ padding: "16px" }}>
        <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>

          {/* Icon */}
          <div style={{
            width:44, height:44, borderRadius:12, flexShrink:0,
            background:`rgba(${hexToRgb(r.color)},0.1)`,
            border:`1px solid rgba(${hexToRgb(r.color)},0.3)`,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:"1.4rem",
          }}>
            {r.icon}
          </div>

          {/* Title + price */}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
              <span style={{ color:RT.text, fontWeight:700, fontSize:"1.05rem",
                             fontFamily:RT.font }}>
                {r.title}
              </span>
              <span style={{
                padding:"2px 8px", borderRadius:12, fontSize:"0.9rem", fontWeight:700,
                background: isAvailable
                  ? `rgba(${hexToRgb(r.color)},0.12)`
                  : "#f0f0f0",
                border: `1px solid ${isAvailable ? r.color + "60" : RT.border}`,
                color: isAvailable ? r.color : RT.textDim,
              }}>
                {isAvailable ? (hasGenerated ? "✓ Generated" : "Available") : "Coming Soon"}
              </span>
            </div>
            <div style={{ color:RT.textDim, fontSize:"0.98rem", marginTop:2 }}>
              {r.subtitle}
            </div>
            <div style={{ color:r.color, fontSize:"1.0rem", fontWeight:700, marginTop:4 }}>
              ₹{r.price}
              <span style={{ color:RT.textDim, fontWeight:400 }}> · one-time</span>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <p style={{ color:RT.text, fontSize:"1.02rem", margin:"12px 0 0",
                    lineHeight:1.6, fontStyle:"italic" }}>
          {r.tagline}
        </p>

        {/* Toggle preview */}
        <button
          onClick={() => setExpanded(e => !e)}
          style={{ marginTop:10, padding:"6px 14px", borderRadius:20,
                   background:"transparent",
                   border:`1px solid ${expanded ? r.color + "60" : RT.border}`,
                   color: expanded ? r.color : RT.textDim,
                   fontSize:"0.95rem", fontWeight:600, cursor:"pointer", transition:"all 0.2s" }}>
          {expanded ? "Hide preview ▲" : "See what's inside ▼"}
        </button>
      </div>

      {/* ── Expanded preview ── */}
      {expanded && (
        <div style={{ borderTop:`1px solid ${RT.border}`,
                      padding:"14px 16px 16px" }}>

          {/* Description */}
          <p style={{ color:RT.textDim, fontSize:"1.0rem", lineHeight:1.7,
                      margin:"0 0 14px" }}>
            {r.preview}
          </p>

          {/* What's inside */}
          <div style={{ marginBottom:14 }}>
            <div style={{ color:RT.textDim, fontSize:"0.95rem", fontWeight:700,
                          marginBottom:8 }}>
              What's inside
            </div>
            {r.includes.map((item, i) => (
              <div key={i} style={{ display:"flex", gap:8, alignItems:"flex-start",
                                    padding:"4px 0" }}>
                <span style={{ color:r.color, fontSize:"0.95rem", marginTop:3, flexShrink:0 }}>✦</span>
                <span style={{ color:RT.text, fontSize:"1.0rem", lineHeight:1.5 }}>
                  {item}
                </span>
              </div>
            ))}
          </div>

          {/* Sample quote — always visible (free teaser) */}
          <div style={{ background:RT.cardWarm,
                        borderLeft:`3px solid ${r.color}`,
                        borderRadius:"0 8px 8px 0",
                        padding:"10px 14px", marginBottom: isSubscribed ? 16 : 0 }}>
            <div style={{ color:RT.textDim, fontSize:"0.92rem", fontWeight:700,
                          marginBottom:4 }}>
              Opening paragraph · free preview
            </div>
            <p style={{ color:RT.text, fontSize:"1.0rem", lineHeight:1.7,
                        margin:0, fontStyle:"italic" }}>
              {r.sample}
            </p>
          </div>

          {/* Blur gate — rest of report locked for non-subscribers */}
          {!isSubscribed && isAvailable && (
            <div style={{ position:"relative", marginBottom:16 }}>
              <div style={{ filter:"blur(4px)", userSelect:"none", pointerEvents:"none",
                padding:"10px 14px",
                background:RT.cardWarm, borderRadius:"0 0 8px 8px" }}>
                <p style={{ color:RT.text, fontSize:"1.0rem", lineHeight:1.7, margin:0, fontStyle:"italic" }}>
                  {"Your Lagna lord in the 10th house forms a powerful Dharmakarmadhipati yoga — the kind of chart combination that is rare and deeply significant for both your worldly success and your spiritual path. What this means for you specifically depends on the exact degree and the nakshatra placement, which we analyse in full..."}
                </p>
              </div>
              <div style={{
                position:"absolute", inset:0,
                background:"linear-gradient(180deg,rgba(255,255,255,0) 0%,rgba(255,255,255,0.94) 45%)",
                display:"flex", flexDirection:"column",
                alignItems:"center", justifyContent:"flex-end",
                padding:"16px", borderRadius:8,
              }}>
                <div style={{ fontSize:"0.95rem", color:RT.textDim, fontWeight:600,
                  marginBottom:8 }}>
                  Full report · ₹{r.price}
                </div>
                <button onClick={() => onOpen(r.id)} style={{
                  background:r.color,
                  border:"none", borderRadius:"10px", padding:"10px 28px",
                  color:"#fff", fontSize:"1.0rem", fontWeight:700,
                  cursor:"pointer",
                  boxShadow:`0 4px 18px ${r.color}55`,
                }}>
                  Generate My Full Report — ₹{r.price}
                </button>
              </div>
            </div>
          )}

          {/* CTA */}
          {isAvailable ? (
            <button
              onClick={() => onOpen(r.id)}
              style={{ width:"100%", padding:"12px", borderRadius:24,
                       background:r.color,
                       border:"none", color:"#fff", fontSize:"1.02rem",
                       fontWeight:700, cursor:"pointer" }}>
              {hasGenerated ? "View My Report →" : "Generate My Report — ₹" + r.price}
            </button>
          ) : (
            <div style={{ width:"100%", padding:"12px", borderRadius:24, textAlign:"center",
                          background:"#fafafa",
                          border:`1px solid ${RT.border}`,
                          color:RT.textDim, fontSize:"1.0rem" }}>
              🔒 Coming Soon
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main exported component ──────────────────────────────────────────────────
export default function ReportsStorePage({ userId, noBirth, onNeedBirth, arcData, onBack, isSubscribed, onUpgrade }) {
  const [activeReport, setActiveReport] = useState(null); // id of opened report
  const [generatedIds, setGeneratedIds] = useState({});   // {id: true}

  // Check which reports are already generated
  useEffect(() => {
    if (!userId) return;
    const checks = [
      { id: "true_self",     url: `/jyotish/report/true-self/${encodeURIComponent(userId)}` },
      { id: "power_purpose", url: `/jyotish/report/power-purpose/${encodeURIComponent(userId)}` },
      { id: "body_energy",   url: `/jyotish/report/body-energy/${encodeURIComponent(userId)}` },
      { id: "career",        url: `/jyotish/report/career/${encodeURIComponent(userId)}` },
      { id: "year_ahead",    url: `/jyotish/report/year-ahead/${encodeURIComponent(userId)}` },
      { id: "next_chapter",  url: `/jyotish/report/next-chapter/${encodeURIComponent(userId)}` },
      { id: "relationship",  url: `/jyotish/report/relationship/${encodeURIComponent(userId)}` },
      { id: "karmic",        url: `/jyotish/report/karmic/${encodeURIComponent(userId)}` },
    ];
    checks.forEach(({ id, url }) => {
      fetch(`${API}${url}`)
        .then(r => r.json())
        .then(d => {
          if (d.sections && d.sections.length > 0) {
            setGeneratedIds(prev => ({ ...prev, [id]: true }));
          }
        })
        .catch(() => {});
    });
  }, [userId]);

  const BackBtn = () => (
    <button
      onClick={() => setActiveReport(null)}
      style={{ display:"flex", alignItems:"center", gap:6, marginBottom:20,
               background:"transparent", border:"none",
               color:RT.textDim, fontSize:"1.0rem", cursor:"pointer",
               padding:0 }}>
      ← Back to Reports
    </button>
  );

  // ── Viewing a specific report ──
  if (activeReport === "true_self") {
    return (
      <div>
        <BackBtn />
        <TrueSelfReportTab
          userId={userId}
          noBirth={noBirth}
          onNeedBirth={onNeedBirth}
          arcData={arcData}
        />
      </div>
    );
  }

  if (activeReport === "power_purpose") {
    return (
      <div>
        <BackBtn />
        <ReportViewerTab
          userId={userId}
          noBirth={noBirth}
          onNeedBirth={onNeedBirth}
          reportType="power_purpose"
          reportEndpoint="/jyotish/report/power-purpose"
          reportName="Your Power & Purpose Reading"
          reportPrice={99}
          accentColor="#a16207"
        />
      </div>
    );
  }

  if (activeReport === "body_energy") {
    return (
      <div>
        <BackBtn />
        <ReportViewerTab
          userId={userId}
          noBirth={noBirth}
          onNeedBirth={onNeedBirth}
          reportType="body_energy"
          reportEndpoint="/jyotish/report/body-energy"
          reportName="Your Body & Energy Reading"
          reportPrice={99}
          accentColor="#15803d"
        />
      </div>
    );
  }

  if (activeReport === "career") {
    return (
      <div>
        <BackBtn />
        <ReportViewerTab
          userId={userId} noBirth={noBirth} onNeedBirth={onNeedBirth}
          reportType="career" reportEndpoint="/jyotish/report/career"
          reportName="Your Life's Work — Career Reading"
          reportPrice={149} accentColor="#4338ca"
        />
      </div>
    );
  }

  if (activeReport === "year_ahead") {
    return (
      <div>
        <BackBtn />
        <ReportViewerTab
          userId={userId} noBirth={noBirth} onNeedBirth={onNeedBirth}
          reportType="year_ahead" reportEndpoint="/jyotish/report/year-ahead"
          reportName="Your Year Ahead — next 12 months"
          reportPrice={149} accentColor="#0f766e"
        />
      </div>
    );
  }

  if (activeReport === "next_chapter") {
    return (
      <div>
        <BackBtn />
        <ReportViewerTab
          userId={userId} noBirth={noBirth} onNeedBirth={onNeedBirth}
          reportType="next_chapter" reportEndpoint="/jyotish/report/next-chapter"
          reportName="Your Next Chapter — 6-Month Reading"
          reportPrice={149} accentColor="#c2410c"
        />
      </div>
    );
  }

  if (activeReport === "relationship") {
    return (
      <div>
        <BackBtn />
        <ReportViewerTab
          userId={userId} noBirth={noBirth} onNeedBirth={onNeedBirth}
          reportType="relationship" reportEndpoint="/jyotish/report/relationship"
          reportName="Your Relationship Blueprint"
          reportPrice={179} accentColor="#be185d"
        />
      </div>
    );
  }

  if (activeReport === "karmic") {
    return (
      <div>
        <BackBtn />
        <ReportViewerTab
          userId={userId} noBirth={noBirth} onNeedBirth={onNeedBirth}
          reportType="karmic" reportEndpoint="/jyotish/report/karmic"
          reportName="Your Karmic Blueprint — Past Life Reading"
          reportPrice={179} accentColor="#7e22ce"
        />
      </div>
    );
  }

  if (activeReport === "muhurta") {
    return (
      <div>
        <BackBtn />
        <MuhurtaReportTab
          userId={userId}
          noBirth={noBirth}
          onNeedBirth={onNeedBirth}
        />
      </div>
    );
  }

  // ── Store listing ──
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0 }}>

      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:"0.95rem", color:RT.accent, fontWeight:700,
                      marginBottom:6 }}>
          Layer 3 · One-time Reports
        </div>
        <div style={{ fontFamily:RT.font, fontSize:"1.3rem", fontWeight:700,
                      color:RT.text, marginBottom:8 }}>
          ✨ Vedic Deep Reads
        </div>
        <p style={{ color:RT.textDim, fontSize:"1.02rem", lineHeight:1.6, margin:0 }}>
          Generated once from your real Janma Kundali. Never expires.
          Each report is personal to you — not generic horoscope content.
        </p>
      </div>

      {/* Price tier pills */}
      <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
        {PRICE_TIERS.map(t => (
          <div key={t.price} style={{
            padding:"5px 12px", borderRadius:20,
            background:RT.cardWarm, border:`1px solid ${RT.border}`,
            fontSize:"0.95rem",
          }}>
            <span style={{ color:RT.accent, fontWeight:700 }}>{t.label}</span>
            <span style={{ color:RT.textDim }}> · {t.desc}</span>
          </div>
        ))}
      </div>

      {/* Family moments + real astrologer (one-time orders, see MomentReportsPage / ConsultPage) */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:12, marginBottom:20 }}>
        {[
          ["family-reports", "💑 Marriage match or baby names", "Detailed Kundali Milan ₹249 · Baby names by nakshatra ₹99"],
          ["consult", "🧑‍🏫 Talk to a real astrologer", "15-minute chat with a verified astrologer · ₹199"],
        ].map(([pg, title, sub]) => (
          <button key={pg} onClick={() => window.dispatchEvent(new CustomEvent("vs:navigate", { detail: pg }))}
            style={{ textAlign:"left", background:RT.cardWarm, border:`1.5px solid ${RT.border}`, borderRadius:16,
                     padding:"16px 18px", cursor:"pointer", fontFamily:"inherit" }}>
            <div style={{ fontSize:"1.15rem", fontWeight:700, color:RT.text, marginBottom:4 }}>{title}</div>
            <div style={{ fontSize:"1rem", color:RT.textDim, lineHeight:1.5 }}>{sub}</div>
          </button>
        ))}
      </div>

      {/* Report cards */}
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {REPORTS.filter(r => r.status !== "coming_soon").map(r => (
          <ReportCard
            key={r.id}
            report={r}
            onOpen={id => setActiveReport(id)}
            hasGenerated={!!generatedIds[r.id]}
            isSubscribed={isSubscribed}
            onUpgrade={onUpgrade}
          />
        ))}
      </div>

      {/* Footer note */}
      <div style={{ marginTop:24, padding:"14px 16px", borderRadius:12,
                    background:RT.cardWarm,
                    border:`1px solid ${RT.border}`,
                    textAlign:"center" }}>
        <p style={{ color:RT.textDim, fontSize:"0.95rem", lineHeight:1.6, margin:0 }}>
          Reports are generated from your stored birth chart data using Vedic
          calculations (Swiss Ephemeris). Each report is unique to your exact
          birth date, time, and place.
        </p>
      </div>
    </div>
  );
}

export { REPORTS };
